// Currency selector — tiny header-top-bar dropdown. Buyers pick their
// local currency and every per-gram price across the site re-renders
// in that currency via the useCurrency() hook.
//
// State is in localStorage (no React context needed — the price
// displays subscribe to the storage event so cross-tab updates work
// too). The hook returns both the code and a stable format() helper.

import { useEffect, useState, useCallback } from "react";
import { CURRENCIES, CURRENCY_LIST, type CurrencyCode, detectInitialCurrency, setCurrency, formatCurrency } from "@/lib/currency";

/**
 * React hook — subscribe to the visitor's chosen currency. Re-renders
 * whenever they change it (this tab or another tab via storage event).
 *
 * Returns the code, the CURRENCIES metadata for that code, and a
 * formatUsd helper. Components call formatUsd(perGramUsd) for display
 * without needing to import currency.ts directly.
 */
export const useCurrency = () => {
  const [code, setCode] = useState<CurrencyCode>(() => detectInitialCurrency());
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "longshine-currency" && e.newValue && CURRENCIES[e.newValue as CurrencyCode]) {
        setCode(e.newValue as CurrencyCode);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  const change = useCallback((next: CurrencyCode) => {
    setCurrency(next);
    setCode(next);
    // Fire storage event manually for THIS tab (the browser only
    // fires it for other tabs).
    window.dispatchEvent(new StorageEvent("storage", { key: "longshine-currency", newValue: next }));
  }, []);
  return {
    code,
    info: CURRENCIES[code],
    formatUsd: (usd: number) => formatCurrency(usd, code),
    change,
  };
};

/**
 * Compact dropdown for the header top bar. Renders the current
 * currency code + symbol; on click opens a list of all supported
 * currencies. Keyboard-accessible (Enter / Space / arrow keys
 * supported via native <select>).
 */
const CurrencySwitcher = () => {
  const { code, change } = useCurrency();
  return (
    <label className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors cursor-pointer">
      <span aria-hidden>{CURRENCIES[code].symbol}</span>
      <select
        aria-label="Select display currency"
        value={code}
        onChange={(e) => change(e.target.value as CurrencyCode)}
        className="bg-transparent border-none cursor-pointer outline-none focus:text-primary"
      >
        {CURRENCY_LIST.map((c) => (
          <option key={c} value={c}>{c}</option>
        ))}
      </select>
    </label>
  );
};

export default CurrencySwitcher;
